import { motion } from "framer-motion";
import { Plane, Ship, FileCheck2, PackageCheck, ArrowUpRight } from "lucide-react";

const AIR_IMG =
  "https://images.unsplash.com/photo-1571086291540-b137111fa1c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxhaXIlMjBjYXJnbyUyMHBsYW5lfGVufDB8fHx8MTc4Njk2MjA2NXww&ixlib=rb-4.1.0&q=85";
const OCEAN_IMG =
  "https://images.unsplash.com/photo-1606185540834-d6e7483ee1a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxvY2VhbiUyMGZyZWlnaHQlMjBjYXJnbyUyMHNoaXB8ZW58MHx8fHwxNzg2OTYyMDY1fDA&ixlib=rb-4.1.0&q=85";
const DELIVERY_IMG =
  "https://images.pexels.com/photos/8989470/pexels-photo-8989470.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const SERVICES = [
  {
    id: "air-freight",
    num: "01",
    icon: Plane,
    title: "Air Freight",
    desc: "Swift, efficient air cargo into Jamaica. Weekly consolidations keep your packages moving fast.",
    img: AIR_IMG,
    span: "md:col-span-7",
  },
  {
    id: "ocean-freight",
    num: "02",
    icon: Ship,
    title: "Ocean Freight",
    desc: "Big or bulky? Sea freight handles barrels, boxes and furniture at serious savings.",
    img: OCEAN_IMG,
    span: "md:col-span-5",
  },
  {
    id: "customs-clearance",
    num: "03",
    icon: FileCheck2,
    title: "Customs Clearance",
    desc: "We handle the paperwork and duties end-to-end, so your shipment clears without the headache.",
    img: null,
    span: "md:col-span-5",
  },
  {
    id: "door-to-door",
    num: "04",
    icon: PackageCheck,
    title: "Door-to-Door Delivery",
    desc: "From our US mailbox straight to your door — we deliver in and around Spanish Town, St Catherine.",
    img: DELIVERY_IMG,
    span: "md:col-span-7",
  },
];

export const Services = () => (
  <section id="services" data-testid="services-section" className="py-28 sm:py-36">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="mb-16"
      >
        <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#FF8A00] mb-4">
          What we do
        </p>
        <h2 className="font-display font-800 tracking-tighter text-4xl sm:text-5xl lg:text-6xl max-w-2xl leading-[1.02]">
          A complete suite of shipping solutions
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {SERVICES.map((s, i) => (
          <motion.div
            key={s.id}
            data-testid={`service-card-${s.id}`}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: (i % 2) * 0.12 }}
            className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-[#1C0D2C]/60 inner-glow min-h-[19rem] ${s.span}`}
          >
            {s.img && (
              <>
                <img
                  src={s.img}
                  alt={s.title}
                  className="absolute inset-0 w-full h-full object-cover opacity-30 transition-[opacity,transform] duration-700 group-hover:opacity-50 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#12071F] via-[#12071F]/60 to-transparent" />
              </>
            )}
            <div className="relative h-full flex flex-col justify-between p-7 sm:p-9">
              <div className="flex items-start justify-between">
                <span className="font-mono-accent text-sm text-[#FF8A00]">{s.num}</span>
                <s.icon className="w-7 h-7 text-[#FF8A00]" />
              </div>
              <div>
                <h3 className="font-display font-800 tracking-tight text-2xl sm:text-3xl flex items-center gap-2">
                  {s.title}
                  <ArrowUpRight className="w-5 h-5 text-[#FF8A00] opacity-0 -translate-x-1 translate-y-1 transition-[opacity,transform] duration-300 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0" />
                </h3>
                <p className="mt-2 text-sm sm:text-base text-[#A29DB0] max-w-md leading-relaxed">
                  {s.desc}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
